import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Select,
  MenuItem,
} from '@mui/material';
import ApiBasedAutoComplete from '../../../globalComponents/apiBasedAutoComplete';
import { DataLoggerDetails, RemoteControlEquipment } from '../hooks/useDataLoggerDetails';

interface ConfigurationDialogFirstTabProps {
  formData: Partial<DataLoggerDetails> & { remoteControlEquipments?: RemoteControlEquipment[] };
  errors: { [key: string]: string };
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  onInputChange: (field: string, value: any) => void;
  onAssetChange: (asset: any) => void;
  initialData?: {
    serialNumber?: string;
    id?: string;
  };
}

export default function ConfigurationDialogFirstTab({
  formData,
  errors,
  setErrors,
  onInputChange,
  onAssetChange,
  initialData,
}: ConfigurationDialogFirstTabProps) {
  const { t } = useTranslation();





  const handleAssetSelect = (selectedAsset: any) => {
    if (errors.asset) {
      setErrors((prevErrors: { [key: string]: string }) => {
        const newErrors = { ...prevErrors };
        delete newErrors.asset;
        return newErrors;
      });
    }
    onAssetChange(selectedAsset);
  };

  const handleFieldChange = (field: string, value: any) => {
    if (errors[field]) {
      setErrors((prevErrors: { [key: string]: string }) => {
        const newErrors = { ...prevErrors };
        delete newErrors[field];
        return newErrors;
      });
    }
    onInputChange(field, value);
  };


  const handleEquipmentSelect = (selected: any[], event: any) => {
    if (errors.remoteControlEquipment) {
      setErrors((prevErrors: { [key: string]: string }) => {
        const newErrors = { ...prevErrors };
        delete newErrors.remoteControlEquipment;
        return newErrors;
      });
    }
    const prev = Array.isArray(formData.remoteControlEquipments) ? formData.remoteControlEquipments : [];
    const newList: RemoteControlEquipment[] = selected.map(equip => {
      const id = equip._id || equip.equipmentId;
      const existing = prev.find(eq => eq.equipmentId === id);
      if (existing) {
        return { ...existing, isDeleted: false };
      }
      return {
        equipmentName: equip.name || equip.equipmentName,
        equipmentId: id,
        bidirectionalId: equip.bidirectionalId,
        isNewEntry: true,
      };
    });
    const deleted = prev
      .filter(eq => !newList.some(n => n.equipmentId === eq.equipmentId))
      .map(eq => ({ ...eq, isDeleted: true }));
    onInputChange('remoteControlEquipments', [...newList, ...deleted]);
  };

  const nonDeletedEquipments = (formData.remoteControlEquipments || []).filter(eq => !eq.isDeleted);

  return (
    <Box>

      
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <span style={{ fontWeight: 'bold' }}>{t('dataLogger.firstTab.dataLoggerIdLabel', 'Data Logger ID:')}</span> {initialData?.id || t('dataLogger.firstTab.new', 'New')}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <span style={{ fontWeight: 'bold' }}>{t('dataLogger.firstTab.serialNumberLabel', 'Serial Number:')}</span> {formData.serialNumber}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('dataLogger.firstTab.name', 'Name')}
            value={formData.name || ''}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            required
            error={!!errors.name}
            helperText={errors.name}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('dataLogger.firstTab.serialNumber', 'Serial Number')}
            value={formData.serialNumber || ''}
            onChange={(e) => handleFieldChange('serialNumber', e.target.value)}
            required
            error={!!errors.serialNumber}
            helperText={errors.serialNumber}
            disabled={true}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.debug || false}
                onChange={(e) => onInputChange('debug', e.target.checked)}
              />
            }
            label={t('dataLogger.firstTab.debugMode', 'Debug Mode')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Select
            fullWidth
            value={formData.plcBrand || ''}
            onChange={(e) => handleFieldChange('plcBrand', e.target.value)}
            displayEmpty
            required
            error={!!errors.plcBrand}
            renderValue={(selected) => selected || ''}
          >
            <MenuItem value="" disabled>{t('dataLogger.firstTab.selectPlcBrand', 'Select PLC Brand')}</MenuItem>
            <MenuItem value="Fuji">{t('dataLogger.firstTab.plcBrandFuji', 'Fuji')}</MenuItem>
            <MenuItem value="Delta">{t('dataLogger.firstTab.plcBrandDelta', 'Delta')}</MenuItem>
            <MenuItem value="Siemens">{t('dataLogger.firstTab.plcBrandSiemens', 'Siemens')}</MenuItem>
          </Select>
          {errors.plcBrand && (
            <Typography color="error" variant="caption">{errors.plcBrand}</Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('dataLogger.firstTab.pollingIntervalSeconds', 'Polling Interval (seconds)')}
            type="number"
            value={formData.pollingInterval || ''}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (value < 0) return;
              handleFieldChange('pollingInterval', value);
            }}
            onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
            required
            error={!!errors.pollingInterval}
            helperText={errors.pollingInterval}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('dataLogger.firstTab.plcModel', 'PLC Model')}
            value={formData.plcModel || ''}
            onChange={(e) => handleFieldChange('plcModel', e.target.value)}
            required
            error={!!errors.plcModel}
            helperText={errors.plcModel}
            disabled={true}
          />
        </Grid>

        <Grid item xs={12}>
        <ApiBasedAutoComplete
        label={t('dataLogger.firstTab.asset', 'Asset')}
        required
        error={!!errors.asset}
        helperText={errors.asset}
        onSelect={handleAssetSelect}
        apiPath="/plants/getListOfPlantsForAdministrativeUser"
        searchKey="plantName"
        keyToPick="plantName"
        selectedOptions={formData.asset}
        getOptionLabel={(option: any) => option?.plantName || ''}
        isOptionEqualToValue={(option: any, value: any) => option._id === value._id}
        />
        </Grid>

        <Grid item xs={12}>
          <ApiBasedAutoComplete
            label={t('dataLogger.firstTab.remoteControlEquipment', 'Remote Control Equipment')}
            disabled={!formData.asset || !formData.asset._id}
            error={!!errors.remoteControlEquipment}
            helperText={errors.remoteControlEquipment}
            searchKey='equipmentName'
            keyToPick="equipmentName"
            onSelect={handleEquipmentSelect}
            multiple={true}
            apiPath='/data-logger/getRemoteControlEquipments'
            getOptionLabel={(option: any) => option?.name || option?.equipmentName || ''}
            isOptionEqualToValue={(option: any, value: any) => (option._id || option.equipmentId) === (value._id || value.equipmentId)}
            selectedOptions={nonDeletedEquipments}
            defaultData={{assetId : formData?.asset?._id}}
          />
        </Grid>
        

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('dataLogger.firstTab.modbusHost', 'Modbus Host')}
            value={formData.modbusHost || ''}
            onChange={(e) => handleFieldChange('modbusHost', e.target.value)}
            required
            error={!!errors.modbusHost}
            helperText={errors.modbusHost}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('dataLogger.firstTab.modbusPort', 'Modbus Port')}
            type="number"
            value={formData.modbusPort || ''}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (value < 0) return;
              handleFieldChange('modbusPort', value);
            }}
            onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
            required
            error={!!errors.modbusPort}
            helperText={errors.modbusPort}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography sx={{ mb: 2, mt: 2, color: "text.secondary", fontSize: "16px", fontWeight: "600" }}>
            {t('dataLogger.firstTab.realRegistersConfiguration', 'Real Registers Configuration')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t('dataLogger.firstTab.unitIdReal', 'Unit ID Real')}
            type="number"
            value={formData.unitIdReal || ''}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (value < 0) return;
              handleFieldChange('unitIdReal', value);
            }}
            onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
            required
            error={!!errors.unitIdReal}
            helperText={errors.unitIdReal}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t('dataLogger.firstTab.startAddressReal', 'Start Address Real')}
            type="number"
            value={formData.startAddressReal || ''}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (value < 0) return;
              handleFieldChange('startAddressReal', value);
            }}
            onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
            required
            error={!!errors.startAddressReal}
            helperText={errors.startAddressReal}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t('dataLogger.firstTab.registerCountReal', 'Register Count Real')}
            type="number"
            value={formData.registerCountReal || ''}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (value < 0) return;
              handleFieldChange('registerCountReal', value);
            }}
            onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
            required
            error={!!errors.registerCountReal}
            helperText={errors.registerCountReal}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography sx={{ mb: 2, mt: 2, color: "text.secondary", fontSize: "16px", fontWeight: "600" }}>
            {t('dataLogger.firstTab.booleanRegistersConfiguration', 'Boolean Registers Configuration')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t('dataLogger.firstTab.unitIdBoolean', 'Unit ID Boolean')}
            type="number"
            value={formData.unitIdBool || ''}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (value < 0) return;
              handleFieldChange('unitIdBool', value);
            }}
            onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
            required
            error={!!errors.unitIdBool}
            helperText={errors.unitIdBool}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t('dataLogger.firstTab.startAddressBoolean', 'Start Address Boolean')}
            type="number"
            value={formData.startAddressBool || ''}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (value < 0) return;
              handleFieldChange('startAddressBool', value);
            }}
            onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
            required
            error={!!errors.startAddressBool}
            helperText={errors.startAddressBool}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t('dataLogger.firstTab.registerCountBoolean', 'Register Count Boolean')}
            type="number"
            value={formData.registerCountBool || ''}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (value < 0) return;
              handleFieldChange('registerCountBool', value);
            }}
            required
            error={!!errors.registerCountBool}
            helperText={errors.registerCountBool}
          />
        </Grid>
      </Grid>
    </Box>
  );
} 